import React, { createContext, useContext, useState, useEffect } from 'react';
import { Spin } from 'antd';
import { getAuthToken, setAuthToken, getClientType } from '../services/clientConfig';
import { useLoading } from './LoadingContext';

// Create the Auth Context
const AuthContext = createContext(null);

// Track initialization globally (outside component to persist across re-mounts)
let globalInitialized = false;
let globalAuthState = null;

/**
 * AuthProvider Component
 * Handles authentication state, token storage, and user verification
 * Blocks access to the app if user is not authenticated
 */
export function AuthProvider({ children }) {
  const { showLoader, hideLoader } = useLoading();
  // Initialize state from global cache if available (for route changes)
  const [user, setUser] = useState(globalAuthState?.user || null);
  const [token, setToken] = useState(globalAuthState?.token || getAuthToken() || null);
  const [isLoading, setIsLoading] = useState(!globalInitialized);
  const [isAuthenticated, setIsAuthenticated] = useState(globalAuthState?.isAuthenticated || false);
  const [error, setError] = useState(null);

  // Initialize auth state from URL params or localStorage - runs only once globally
  useEffect(() => {
    // Skip if already initialized globally
    if (globalInitialized) {
      return;
    }

    const initializeAuth = async () => {
      globalInitialized = true;
      setIsLoading(true);
      showLoader("VERIFICA AUTENTICAZIONE...");
      setError(null);

      // Initialize auth token and client type once at app startup.
      // Both are extracted from URL query params (if present) and stored in localStorage.
      const authToken = getAuthToken();
      getClientType();

      // Update state with the token we obtained (if any)
      setToken(authToken);

      // ============================================================
      // DEV MODE: Token verification disabled for local testing.
      // Uncomment the block below (and comment out the bypass)
      // when re-enabling authentication.
      // ============================================================

      // // --- BYPASS START ---
      // setIsAuthenticated(true);
      // setUser({ all_in_one_services: ['fcd_network', 'simfix_areas', 'predict', 'tl_green_wave', 'signalfix', 'od_analysis', 'assignx'] });
      // globalAuthState = { user: { all_in_one_services: ['fcd_network', 'simfix_areas', 'predict', 'tl_green_wave', 'signalfix', 'od_analysis', 'assignx'] }, token: authToken, isAuthenticated: true };
      // // --- BYPASS END ---

      // --- PRODUCTION AUTH (re-enable when token is available) ---
      if (authToken) {
        setToken(authToken);

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            if (userData?.company) {
              localStorage.setItem('company', userData.company);
            }
            setUser(userData);
            setIsAuthenticated(true);
            globalAuthState = { user: userData, token: authToken, isAuthenticated: true };
          } else {
            console.warn('Auth token is invalid or expired');
            setToken(null);
            setIsAuthenticated(false);
            setError('Token non valido o scaduto');
            globalAuthState = null;
          }
        } catch (err) {
          console.error('Error verifying auth token:', err);
          setError('Errore di connessione durante la verifica');
          setIsAuthenticated(false);
          globalAuthState = null;
        }
      } else {
        setIsAuthenticated(false);
        globalAuthState = null;
      }
      // --- END PRODUCTION AUTH --- 

      setIsLoading(false);
      hideLoader();
    };

    initializeAuth();
  }, []); // Empty dependency array - run only once on mount

  // Login function - can be used to manually set token
  const login = async (newToken) => {
    if (!newToken) return false;

    setAuthToken(newToken);
    setToken(newToken);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${newToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData?.company) {
          localStorage.setItem('company', userData.company);
        }
        setUser(userData);
        setIsAuthenticated(true);
        setError(null);
        return true;
      } else {
        setAuthToken(null);
        setToken(null);
        setIsAuthenticated(false);
        setError('Token non valido');
        return false;
      }
    } catch (err) {
      console.error('Error during login:', err);
      setError('Errore di connessione');
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setAuthToken(null);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData?.company) {
          localStorage.setItem('company', userData.company);
        }
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      } else {
        logout();
        return false;
      }
    } catch (err) {
      console.error('Error refreshing user:', err);
      return false;
    }
  };

  // Get allowed services from user data
  const allowedServices = user?.all_in_one_services || [];
  const allowedPermissions = user?.permissions || [];
  // Helper function to check if a service is allowed
  const hasService = (serviceKey) => {
    const services = allowedServices.includes(serviceKey);
    const permissions = allowedPermissions.includes(serviceKey)
    return services || permissions
  };

  // Context value
  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    error,
    login,
    logout,
    refreshUser,
    allowedServices,
    allowedPermissions,
    hasService,
  };

  // Show loading spinner while checking authentication (Visuals handled by showLoader in useEffect)
  if (isLoading) {
    return null;
  }

  /* DEV MODE: Access denied screen disabled for local testing.
  // Re-enable this block when authentication is active.
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #353A0A 100%)',
      }}>
        <Result
          icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
          status="403"
          title={<span style={{ color: '#fff' }}>Accesso Negato</span>}
          subTitle={
            <span style={{ color: '#a0a0a0' }}>
              {error || 'Token di autenticazione mancante o non valido. Effettua il login per accedere.'}
            </span>
          }
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            padding: '40px',
            backdropFilter: 'blur(10px)',
          }}
        />
      </div>
    );
  }
  */

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use the Auth Context
 * @returns {Object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
